(() => {
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  const mobileMotion = window.matchMedia("(pointer: coarse), (max-width: 768px)");

  const initGlassHeader = (options = {}) => {
    const trackScroll = options.trackScroll !== false;
    const header = document.querySelector("[data-rb-glass-header]");
    const shell = document.querySelector("[data-rb-parallax-shell]");
    let headerFrame = 0;

    const update = () => {
      headerFrame = 0;
      const scrollY = window.scrollY || document.documentElement.scrollTop || 0;

      if (header) {
        header.classList.toggle("is-scrolled", scrollY > 18);
        header.style.setProperty("--rb-glass-x", `${Math.min(86, Math.max(14, 50 + scrollY * 0.035))}%`);
      }

      if (shell && !mobileMotion.matches && !reducedMotion.matches) {
        const parallaxY = Math.min(18, scrollY * 0.018);
        shell.style.setProperty("--rb-shell-parallax", parallaxY.toFixed(2));
      }
    };

    const requestUpdate = () => {
      if (headerFrame) {
        return;
      }

      headerFrame = window.requestAnimationFrame(update);
    };

    if (trackScroll) {
      window.addEventListener("scroll", requestUpdate, { passive: true });
      window.addEventListener("resize", requestUpdate);
      window.addEventListener(
        "pointermove",
        (event) => {
          if (!header || event.pointerType !== "mouse") {
            return;
          }

          header.style.setProperty("--rb-glass-x", `${Math.min(92, Math.max(8, (event.clientX / Math.max(window.innerWidth, 1)) * 100))}%`);
        },
        { passive: true },
      );

      update();
      return;
    }

    headerFrame = 0;
    if (header) {
      header.classList.remove("is-scrolled");
      header.style.removeProperty("--rb-glass-x");
    }
  };

  const initNewsletterForms = () => {
    document.querySelectorAll("[data-rb-newsletter]").forEach((form) => {
      const status = form.querySelector("[data-rb-newsletter-status]");
      const submit = form.querySelector(".rb-svg-page__newsletter-submit, button[type='submit']");

      form.querySelectorAll("input").forEach((input) => {
        input.addEventListener("input", () => {
          form.classList.remove("ErrorState");
          form.classList.remove("SuccessState");

          if (status) {
            status.textContent = "";
          }

          if (submit && submit.textContent === "Sent") {
            submit.textContent = "Enter";
          }
        });
      });

      form.addEventListener("submit", (event) => {
        event.preventDefault();

        if (!form.checkValidity()) {
          form.classList.remove("ErrorState");
          form.classList.remove("SuccessState");
          void form.offsetWidth;
          form.classList.add("ErrorState");

          if (status) {
            status.textContent = "Enter a valid email address.";
          }

          if (submit) {
            submit.textContent = "Enter";
          }

          return;
        }

        form.classList.remove("ErrorState");
        form.classList.add("SuccessState");

        if (status) {
          status.textContent = "Thank you. You're on the list.";
        }

        if (submit) {
          submit.textContent = "Sent";
        }
      });
    });
  };

  const initProductPhotoRails = () => {
    document.querySelectorAll(".rb-product-carousel__card").forEach((card) => {
      const rail = card.querySelector(".rb-product-carousel__photo-rail");
      const previous = card.querySelector("[data-rb-photo-prev]");
      const next = card.querySelector("[data-rb-photo-next]");

      if (!rail || !previous || !next) {
        return;
      }

      const scrollBySlide = (direction) => {
        rail.classList.add("is-changing");
        rail.scrollBy({
          left: direction * rail.clientWidth,
          behavior: reducedMotion.matches ? "auto" : "smooth",
        });
        window.setTimeout(() => rail.classList.remove("is-changing"), reducedMotion.matches ? 0 : 220);
      };

      previous.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        scrollBySlide(-1);
      });

      next.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        scrollBySlide(1);
      });
    });
  };

  const initMagneticButtons = () => {
    if (reducedMotion.matches || mobileMotion.matches) {
      return;
    }

    const desktopPointer = window.matchMedia("(hover: hover) and (pointer: fine)");
    const saveData = Boolean(navigator.connection && navigator.connection.saveData);
    const buttonStrength = 0.4;
    const labelStrength = 0.24;

    document.querySelectorAll("[data-rb-magnetic-zone]").forEach((zone) => {
      const button = zone.querySelector("[data-rb-magnetic-button]");
      const label = zone.querySelector("[data-rb-magnetic-label]");
      const soundSrc = zone.getAttribute("data-rb-hover-sound");
      let resetTimer = 0;
      let hoverAudio = null;
      let audioFrame = 0;
      let audioVolume = 0;
      let audioActive = false;

      if (!button || !label) {
        return;
      }

      const setAudioVolume = (nextVolume) => {
        audioVolume = clamp(nextVolume, 0, 1);

        if (hoverAudio) {
          hoverAudio.volume = audioVolume;
        }
      };

      const fadeAudioTo = (targetVolume, options = {}) => {
        if (!hoverAudio) {
          return;
        }

        window.cancelAnimationFrame(audioFrame);

        const fromVolume = audioVolume;
        const toVolume = clamp(targetVolume, 0, 1);
        const duration = options.duration || 220;
        const startedAt = performance.now();

        const tick = (now) => {
          const progress = clamp((now - startedAt) / duration, 0, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          setAudioVolume(fromVolume + (toVolume - fromVolume) * eased);

          if (progress < 1) {
            audioFrame = window.requestAnimationFrame(tick);
            return;
          }

          audioFrame = 0;

          if (toVolume === 0 && !audioActive) {
            hoverAudio.pause();
          }
        };

        audioFrame = window.requestAnimationFrame(tick);
      };

      const ensureHoverAudio = () => {
        if (!soundSrc || saveData || !desktopPointer.matches) {
          return null;
        }

        if (!hoverAudio) {
          hoverAudio = new Audio(soundSrc);
          hoverAudio.loop = true;
          hoverAudio.preload = "auto";
          hoverAudio.volume = 0;
          hoverAudio.load();
        }

        return hoverAudio;
      };

      const startHoverAudio = () => {
        const audio = ensureHoverAudio();

        if (!audio) {
          return;
        }

        audioActive = true;

        if (audio.paused) {
          audio.play().catch(() => {
            audioActive = false;
          });
        }

        fadeAudioTo(0.34, { duration: 180 });
      };

      const startDesktopHoverAudio = (event) => {
        if (!desktopPointer.matches) {
          return;
        }

        if (event && "pointerType" in event && event.pointerType !== "mouse") {
          return;
        }

        startHoverAudio();
      };

      const stopHoverAudio = () => {
        if (!hoverAudio) {
          return;
        }

        audioActive = false;
        fadeAudioTo(0, { duration: 360 });
      };

      const reset = () => {
        window.clearTimeout(resetTimer);
        zone.classList.remove("is-magnetic");
        button.style.setProperty("--rb-magnetic-x", "0px");
        button.style.setProperty("--rb-magnetic-y", "0px");
        label.style.setProperty("--rb-magnetic-label-x", "0px");
        label.style.setProperty("--rb-magnetic-label-y", "0px");
        stopHoverAudio();
      };

      zone.addEventListener(
        "pointerenter",
        startDesktopHoverAudio,
        { passive: true },
      );
      zone.addEventListener("mouseenter", startDesktopHoverAudio, { passive: true });
      zone.addEventListener("focusin", startDesktopHoverAudio);

      zone.addEventListener(
        "pointermove",
        (event) => {
          if (event.pointerType !== "mouse") {
            return;
          }

          const rect = zone.getBoundingClientRect();
          const x = clamp(event.clientX - rect.left, 0, rect.width) - rect.width / 2;
          const y = clamp(event.clientY - rect.top, 0, rect.height) - rect.height / 2;

          window.clearTimeout(resetTimer);
          zone.classList.add("is-magnetic");
          startHoverAudio();
          button.style.setProperty("--rb-magnetic-x", `${(x * buttonStrength).toFixed(2)}px`);
          button.style.setProperty("--rb-magnetic-y", `${(y * buttonStrength).toFixed(2)}px`);
          label.style.setProperty("--rb-magnetic-label-x", `${(x * labelStrength).toFixed(2)}px`);
          label.style.setProperty("--rb-magnetic-label-y", `${(y * labelStrength).toFixed(2)}px`);
        },
        { passive: true },
      );

      zone.addEventListener(
        "pointerdown",
        startDesktopHoverAudio,
        { passive: true },
      );

      zone.addEventListener("pointerleave", reset, { passive: true });
      zone.addEventListener("mouseleave", reset, { passive: true });
      zone.addEventListener("blur", reset, true);
      window.addEventListener("pagehide", reset);
    });
  };

  const initProductCarouselScrollbar = () => {
    document.querySelectorAll(".rb-product-carousel").forEach((carousel) => {
      const track = carousel.querySelector(".rb-product-carousel__track");
      const thumb = carousel.querySelector("[data-rb-carousel-scrollbar]");
      const scrollbar = thumb ? thumb.closest(".rb-product-carousel__scrollbar") : null;

      if (!track || !thumb || !scrollbar) {
        return;
      }

      let isDragging = false;
      let activePointerId = null;
      let dragOffset = 0;
      let dragFrame = 0;
      let pendingClientX = 0;

      const getClientX = (event) => {
        if (typeof event.clientX === "number") {
          return event.clientX;
        }

        return event.touches?.[0]?.clientX || 0;
      };

      const update = () => {
        const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
        const visibleRatio = track.scrollWidth > 0 ? track.clientWidth / track.scrollWidth : 1;
        const scrollbarWidth = scrollbar.getBoundingClientRect().width;
        const thumbWidth = clamp(visibleRatio, 0.18, 1) * scrollbarWidth;
        const travel = Math.max(0, scrollbarWidth - thumbWidth);
        const progress = maxScroll > 0 ? clamp(track.scrollLeft / maxScroll, 0, 1) : 0;

        thumb.style.setProperty("--rb-carousel-thumb-width", `${thumbWidth}px`);
        thumb.style.setProperty("--rb-carousel-thumb-x", `${progress * travel}px`);
      };

      const scrollToClientX = (clientX) => {
        const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
        if (maxScroll <= 0) {
          return;
        }

        const bounds = scrollbar.getBoundingClientRect();
        const thumbWidth = thumb.getBoundingClientRect().width;
        const travel = Math.max(1, bounds.width - thumbWidth);
        const x = clamp(clientX - bounds.left - dragOffset, 0, travel);
        const progress = travel > 0 ? x / travel : 0;
        track.scrollTo({ left: progress * maxScroll, behavior: "auto" });
        update();
      };

      const requestDragScroll = (clientX) => {
        pendingClientX = clientX;

        if (dragFrame) {
          return;
        }

        dragFrame = window.requestAnimationFrame(() => {
          dragFrame = 0;
          scrollToClientX(pendingClientX);
        });
      };

      scrollbar.addEventListener("pointerdown", (event) => {
        event.preventDefault();
        isDragging = true;
        activePointerId = event.pointerId;
        const thumbBounds = thumb.getBoundingClientRect();
        const clientX = getClientX(event);
        dragOffset =
          clientX >= thumbBounds.left && clientX <= thumbBounds.right
            ? clientX - thumbBounds.left
            : thumbBounds.width / 2;
        scrollbar.classList.add("is-dragging");
        carousel.classList.add("is-dragging-carousel");
        scrollbar.setPointerCapture?.(activePointerId);
        requestDragScroll(clientX);
      });

      scrollbar.addEventListener("pointermove", (event) => {
        if (!isDragging || event.pointerId !== activePointerId) {
          return;
        }

        event.preventDefault();
        requestDragScroll(getClientX(event));
      });

      const stopDrag = (event) => {
        if (!isDragging || event.pointerId !== activePointerId) {
          return;
        }

        isDragging = false;
        scrollbar.classList.remove("is-dragging");
        carousel.classList.remove("is-dragging-carousel");

        if (dragFrame) {
          window.cancelAnimationFrame(dragFrame);
          dragFrame = 0;
        }

        if (scrollbar.hasPointerCapture?.(activePointerId)) {
          scrollbar.releasePointerCapture(activePointerId);
        }

        activePointerId = null;
      };

      scrollbar.addEventListener("pointerup", stopDrag);
      scrollbar.addEventListener("pointercancel", stopDrag);
      window.addEventListener("pointerup", stopDrag);
      window.addEventListener("pointercancel", stopDrag);
      track.addEventListener("scroll", () => window.requestAnimationFrame(update), { passive: true });
      window.addEventListener("resize", update, { passive: true });
      update();
    });
  };

  const parallaxTargets = [
    { selector: "#art-2-product-tag-image", speed: 28, drift: -5, cursor: 8, repel: 14 },
    { selector: "#art-3-cap-image", speed: -18, drift: 4, cursor: 6, repel: 12 },
    { selector: "#art-4-product-label-image", speed: 22, drift: -4, cursor: 7, repel: 12 },
    { selector: "#art-5-purple-thread-vector", speed: -30, drift: 7, cursor: 4, repel: 0 },
  ];

  const revealSelectors = [
    "#intro-brand-statement",
    "#services-hero-heading",
    "#craft-proof-heading",
    "#service-product-launches-label-left",
    "#service-events-festivals-label",
    "#service-schools-label",
    "#service-corporate-gifting-label",
    "#service-product-launches-copy-left",
    "#service-product-launches-copy-center",
    "#service-schools-copy",
    "#service-corporate-gifting-copy",
    "#footer-information-heading",
    "#footer-address-block",
    "#footer-location-coordinates",
    "#footer-services-list",
    "#footer-email-link",
    "#footer-phone-link",
    "#newsletter-heading",
    "#newsletter-consent-copy",
  ];

  const parallaxScenes = [];
  const pointer = { active: false, x: 0.5, y: 0.5, clientX: 0, clientY: 0 };
  let parallaxFrame = 0;
  let mobileParallaxFrame = 0;

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  const getSvgDocument = (object) => {
    try {
      return object.contentDocument;
    } catch (error) {
      return null;
    }
  };

  const wrapParallaxLayer = (element) => {
    if (!element || element.parentElement?.dataset.rbParallaxLayer) {
      return element?.parentElement || null;
    }

    const document = element.ownerDocument;
    const wrapper = document.createElementNS("http://www.w3.org/2000/svg", "g");
    const sourceId = element.getAttribute("id") || "unnamed-layer";

    wrapper.dataset.rbParallaxLayer = sourceId;
    wrapper.setAttribute("id", `${sourceId}-parallax-layer`);
    element.parentNode.insertBefore(wrapper, element);
    wrapper.appendChild(element);

    return wrapper;
  };

  const injectSvgAnimationStyles = (svgDocument) => {
    if (svgDocument.getElementById("rebrand-runtime-animation-styles")) {
      return;
    }

    const style = svgDocument.createElementNS("http://www.w3.org/2000/svg", "style");
    style.setAttribute("id", "rebrand-runtime-animation-styles");
    style.textContent = `
      @media (prefers-reduced-motion: no-preference) {
        .rb-svg-reveal {
          opacity: 0;
          transform: translateY(12px);
          transition: opacity 700ms ease, transform 700ms ease;
          transform-box: fill-box;
          transform-origin: center;
          will-change: opacity, transform;
        }

        .rb-svg-reveal.is-visible {
          opacity: 1;
          transform: translateY(0);
        }

        #art-0-hero-background-image,
        #art-0-hero-background-overlay,
        #art-1-footer-editorial-image,
        #art-2-file-button {
          opacity: 0 !important;
          pointer-events: none !important;
        }

        #art-5-purple-thread-vector {
          animation: rb-thread-float 5.6s ease-in-out infinite;
          filter: drop-shadow(0 0 5px rgba(112, 0, 255, 0.22));
          transform-box: fill-box;
          transform-origin: center;
        }

        #art-5-purple-thread-vector path {
          animation: rb-thread-color-pulse 3.8s ease-in-out infinite;
          fill: #7000ff !important;
        }

        #hero-title-group {
          animation: rb-hero-type-shimmer 1200ms ease-out 180ms both;
        }

        #footer-globe-mark {
          animation: rb-footer-globe-orbit 26s linear infinite;
          transform-box: fill-box;
          transform-origin: center;
        }

        #hero-barcode-graphic {
          animation: rb-barcode-settle 900ms ease-out 300ms both;
          transform-box: fill-box;
          transform-origin: center;
        }

        #hero-barcode-graphic::after {
          content: "";
        }

        #hero-barcode-graphic rect {
          animation: rb-barcode-scan 1700ms ease-in-out 550ms both;
        }

        #hero-barcode-graphic rect:nth-child(2n) {
          animation-delay: 650ms;
        }

        #hero-barcode-graphic rect:nth-child(3n) {
          animation-delay: 750ms;
        }

        @keyframes rb-thread-color-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.48; }
        }

        @keyframes rb-thread-float {
          0%, 100% { transform: translate(0, 0) rotate(0deg); }
          50% { transform: translate(0, -10px) rotate(-0.4deg); }
        }

        @keyframes rb-hero-type-shimmer {
          0% { opacity: 0.64; }
          38% { opacity: 1; }
          56% { opacity: 0.82; }
          100% { opacity: 1; }
        }

        @keyframes rb-footer-globe-orbit {
          0% { transform: translate(-34px, 0) rotate(0deg); }
          100% { transform: translate(-34px, 0) rotate(360deg); }
        }

        @keyframes rb-barcode-settle {
          0% { opacity: 0.45; transform: translateY(10px); }
          100% { opacity: 1; transform: translateY(0); }
        }

        @keyframes rb-barcode-scan {
          0% { opacity: 0.28; transform: scaleY(0.38); }
          45% { opacity: 1; transform: scaleY(1.08); }
          100% { opacity: 1; transform: scaleY(1); }
        }
      }

      @media (prefers-reduced-motion: reduce) {
        rect[fill="url(#pattern1_21_703)"],
        rect[fill="url(#pattern2_21_703)"],
        rect[fill="url(#pattern3_21_703)"],
        rect[fill="url(#pattern4_21_703)"],
        rect[fill="url(#pattern5_21_703)"],
        #art-5-purple-thread-vector,
        #art-5-purple-thread-vector path,
        #hero-title-group,
        #footer-globe-mark,
        #hero-barcode-graphic,
        #hero-barcode-graphic rect,
        .rb-svg-reveal {
          animation: none !important;
          transition: none !important;
          transform: none !important;
          opacity: 1 !important;
        }
      }
    `;
    svgDocument.documentElement.appendChild(style);
  };

  const initRevealLayers = (svgDocument) => {
    return revealSelectors
      .map((selector) => svgDocument.querySelector(selector))
      .filter(Boolean)
      .map((element) => {
        element.classList.add("rb-svg-reveal");
        return element;
      });
  };

  const updateRevealLayers = (scene, viewportHeight) => {
    const svg = scene.object.contentDocument?.documentElement;
    const viewBox = svg?.viewBox?.baseVal;

    if (!viewBox || reducedMotion.matches || mobileMotion.matches) {
      scene.revealLayers.forEach((element) => element.classList.add("is-visible"));
      return;
    }

    const rect = scene.object.getBoundingClientRect();
    const scale = rect.width / viewBox.width;

    scene.revealLayers.forEach((element) => {
      const box = element.getBBox();
      const elementTop = rect.top + box.y * scale;
      const revealAt = viewportHeight * 0.86;

      if (elementTop < revealAt) {
        element.classList.add("is-visible");
      }
    });
  };

  const updateParallax = () => {
    parallaxFrame = 0;
    updateScrollProgress();

    if (reducedMotion.matches) {
      parallaxScenes.forEach((scene) => {
        scene.layers.forEach(({ wrapper }) => {
          wrapper.removeAttribute("transform");
        });
        scene.revealLayers.forEach((element) => element.classList.add("is-visible"));
      });
      return;
    }

    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const viewportCenter = viewportHeight / 2;

    parallaxScenes.forEach((scene) => {
      const rect = scene.object.getBoundingClientRect();
      const sceneCenter = rect.top + rect.height / 2;
      const progress = clamp((viewportCenter - sceneCenter) / (rect.height / 2 + viewportHeight), -1, 1);
      const mobileStrength = mobileMotion.matches ? 0.34 : 1;
      const pointerX = pointer.active ? pointer.x - 0.5 : 0;
      const pointerY = pointer.active ? pointer.y - 0.5 : 0;

      scene.layers.forEach(({ wrapper, speed, drift, cursor, repel }) => {
        let repelX = 0;
        let repelY = 0;

        if (pointer.active && scene.viewBox && repel > 0) {
          const scale = rect.width / scene.viewBox.width;
          const box = wrapper.getBBox();
          const centerX = rect.left + (box.x + box.width / 2) * scale;
          const centerY = rect.top + (box.y + box.height / 2) * scale;
          const deltaX = pointer.clientX - centerX;
          const deltaY = pointer.clientY - centerY;
          const distance = Math.hypot(deltaX, deltaY);
          const radius = Math.max(140, Math.min(320, Math.max(box.width, box.height) * scale * 1.7));

          if (distance < radius) {
            const push = (1 - distance / radius) * repel;
            repelX = -(deltaX / Math.max(distance, 1)) * push;
            repelY = -(deltaY / Math.max(distance, 1)) * push;
          }
        }

        const y = progress * speed * mobileStrength + pointerY * cursor + repelY;
        const x = Math.sin(progress * Math.PI) * drift * mobileStrength + pointerX * cursor + repelX;
        wrapper.setAttribute("transform", `translate(${x.toFixed(2)} ${y.toFixed(2)})`);
      });

      updateRevealLayers(scene, viewportHeight);
    });
  };

  const requestParallaxUpdate = () => {
    if (parallaxFrame) {
      return;
    }

    parallaxFrame = window.requestAnimationFrame(updateParallax);
  };

  const handleScroll = () => {
    requestParallaxUpdate();
    requestMobileParallaxUpdate();
  };

  const updateMobileParallax = () => {
    mobileParallaxFrame = 0;

    if (!mobileMotion.matches || reducedMotion.matches) {
      document.querySelectorAll("[data-rb-mobile-parallax]").forEach((element) => {
        element.style.translate = "";
      });
      return;
    }

    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const viewportCenter = viewportHeight / 2;

    document.querySelectorAll("[data-rb-mobile-parallax]").forEach((element) => {
      const depth = Number.parseFloat(element.dataset.rbMobileParallax || "0");
      const rect = element.getBoundingClientRect();
      const elementCenter = rect.top + rect.height / 2;
      const progress = clamp((viewportCenter - elementCenter) / viewportHeight, -1, 1);
      const y = progress * depth * 80;

      element.style.translate = `0 ${y.toFixed(2)}px`;
    });
  };

  const requestMobileParallaxUpdate = () => {
    if (mobileParallaxFrame) {
      return;
    }

    mobileParallaxFrame = window.requestAnimationFrame(updateMobileParallax);
  };

  const updateScrollProgress = () => {
    const progress = document.querySelector("[data-rb-scroll-progress]");

    if (!progress || mobileMotion.matches) {
      return;
    }

    const scrollable = document.documentElement.scrollHeight - window.innerHeight;
    const amount = scrollable > 0 ? clamp(window.scrollY / scrollable, 0, 1) : 0;
    progress.style.transform = `scaleX(${amount.toFixed(4)})`;
  };

  const initParallaxObject = (object) => {
    if (mobileMotion.matches) {
      const svgSource = object.dataset.rbMobileSvgSrc || object.dataset.rbSvgSrc;

      if (svgSource && object.getAttribute("data") !== svgSource) {
        object.setAttribute("data", svgSource);
        object.dataset.rbParallaxReady = "false";
        return;
      }

      const svgDocument = getSvgDocument(object);

      if (svgDocument) {
        injectSvgAnimationStyles(svgDocument);
        initRevealLayers(svgDocument).forEach((element) => element.classList.add("is-visible"));
        object.dataset.rbParallaxReady = "true";
      }

      return;
    }

    const svgSource = object.dataset.rbSvgSrc;

    if (svgSource && object.getAttribute("data") !== svgSource) {
      object.setAttribute("data", svgSource);
      return;
    }

    const svgDocument = getSvgDocument(object);

    if (!svgDocument || object.dataset.rbParallaxReady === "true") {
      return;
    }

    injectSvgAnimationStyles(svgDocument);

    const layers = parallaxTargets
      .map(({ selector, speed, drift, cursor, repel }) => {
        const element = svgDocument.querySelector(selector);
        const wrapper = wrapParallaxLayer(element);

        if (!wrapper) {
          return null;
        }

        return { wrapper, speed, drift, cursor, repel };
      })
      .filter(Boolean);

    const revealLayers = initRevealLayers(svgDocument);
    const viewBox = svgDocument.documentElement.viewBox?.baseVal || null;

    if (!layers.length && !revealLayers.length) {
      return;
    }

    object.dataset.rbParallaxReady = "true";
    const existingSceneIndex = parallaxScenes.findIndex((scene) => scene.object === object);

    if (existingSceneIndex >= 0) {
      parallaxScenes.splice(existingSceneIndex, 1);
    }

    parallaxScenes.push({ object, layers, revealLayers, viewBox });
    requestParallaxUpdate();
  };

  document.querySelectorAll("[data-rb-svg-object]").forEach((object) => {
    object.addEventListener("load", () => initParallaxObject(object));

    initParallaxObject(object);
  });

  window.addEventListener("scroll", handleScroll, { passive: true });
  window.addEventListener("resize", requestParallaxUpdate);
  window.addEventListener("resize", () => {
    document.querySelectorAll("[data-rb-svg-object]").forEach((object) => {
      if (mobileMotion.matches) {
        const svgSource = object.dataset.rbMobileSvgSrc || object.dataset.rbSvgSrc;

        if (svgSource && object.getAttribute("data") !== svgSource) {
          object.setAttribute("data", svgSource);
        }

        object.dataset.rbParallaxReady = "false";
        requestMobileParallaxUpdate();
        return;
      }

      initParallaxObject(object);
    });
  });
  window.addEventListener(
    "pointermove",
    (event) => {
      if (event.pointerType !== "mouse" || reducedMotion.matches || mobileMotion.matches) {
        return;
      }

      pointer.active = true;
      pointer.clientX = event.clientX;
      pointer.clientY = event.clientY;
      pointer.x = clamp(event.clientX / Math.max(window.innerWidth, 1), 0, 1);
      pointer.y = clamp(event.clientY / Math.max(window.innerHeight, 1), 0, 1);
      requestParallaxUpdate();
    },
    { passive: true },
  );
  window.addEventListener(
    "pointerleave",
    () => {
      pointer.active = false;
      requestParallaxUpdate();
    },
    { passive: true },
  );
  reducedMotion.addEventListener?.("change", () => {
    requestParallaxUpdate();
    requestMobileParallaxUpdate();
  });
  mobileMotion.addEventListener?.("change", () => {
    requestParallaxUpdate();
    requestMobileParallaxUpdate();
  });
  updateScrollProgress();
  requestMobileParallaxUpdate();

  initGlassHeader({ trackScroll: !mobileMotion.matches });
  initNewsletterForms();
  initProductPhotoRails();
  initMagneticButtons();
  initProductCarouselScrollbar();
})();
